import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, Printer, Copy, Check, UserX, Search, ShieldCheck, RotateCcw, CheckCircle2, GitMerge, AlertTriangle, Mail, Phone, CalendarClock, FileWarning } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "bg-[#EDF2F4] border border-[#EBE9E4] rounded-lg focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-2.5 py-1.5 text-sm";
const TH = "text-left text-[11px] font-semibold uppercase tracking-wider px-3 py-2";
const TD = "px-3 py-2.5 text-sm align-top";
const muted = { color: "var(--sj-text-3)" };

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const maskVal = (type, v) => (type === "phone" ? `${v.slice(0, 3)}•••${v.slice(-2)}` : v);
const rowLabel = (r) => (r.source_row ? r.source_row : r.source === "portal" ? "Portal" : "—");

const Pill = ({ tone, children }) => {
  const t = { red: ["#FDECEC", "#B42318"], amber: ["#FBEEE6", "#B45309"], blue: ["#E6EEFB", "#1D4ED8"], green: ["#E3F1EC", "#0B5E4E"] }[tone];
  return <span className="inline-block text-[11px] font-medium px-2 py-0.5 rounded-full mr-1 mb-1" style={{ background: t[0], color: t[1] }}>{children}</span>;
};

const SectionHead = ({ title, hint, count }) => (
  <div className="mb-3">
    <div className="flex items-center gap-2">
      <h3 className="font-head text-lg font-semibold" style={{ color: "var(--sj-text)" }}>{title}</h3>
      {count !== undefined && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: count ? "#FBEEE6" : "#E3F1EC", color: count ? "#B45309" : "#0B5E4E" }}>{count}</span>}
    </div>
    <p className="text-xs mt-0.5" style={muted}>{hint}</p>
  </div>
);

const Empty = ({ children }) => <p className="text-sm py-2 flex items-center gap-1.5" style={muted}><CheckCircle2 className="h-4 w-4" style={{ color: "#0B5E4E" }} /> {children}</p>;

const Metric = ({ id, label, value, sub, tone, Icon }) => (
  <div className={`${CARD} p-4`} data-testid={`cell-dq-metric-${id}`}>
    <div className="flex items-center justify-between"><span className="text-xs font-semibold" style={{ color: "var(--sj-text-2)" }}>{label}</span><Icon className="h-4 w-4" style={{ color: tone }} /></div>
    <div className="text-3xl font-semibold mt-1" style={{ color: "var(--sj-text)" }} data-testid={`cell-dq-metric-${id}-value`}>{value}</div>
    <div className="text-[11px] mt-0.5" style={{ color: tone }}>{sub}</div>
  </div>
);

function ReviewTable({ rows }) {
  return (
    <div className={`${CARD} p-5`} data-testid="cell-dq-review-section">
      <SectionHead title="Records requiring review" hint="Records with missing, invalid or potentially duplicated information." count={rows.length} />
      {rows.length === 0 ? <Empty>No records need review.</Empty> : (
        <div className="overflow-x-auto"><table className="w-full min-w-[640px]">
          <thead><tr style={{ background: "#F7F6F3", color: "var(--sj-text-3)" }}><th className={TH}>Row</th><th className={TH}>Name</th><th className={TH}>Postcode entered</th><th className={TH}>Detected postcode</th><th className={TH}>Issues</th></tr></thead>
          <tbody>{rows.map(({ r, issues }) => (
            <tr key={r.record_id} className="border-t" style={{ borderColor: "var(--sj-border)" }} data-testid={`cell-dq-review-row-${r.record_id}`}>
              <td className={TD} style={muted}>{rowLabel(r)}</td>
              <td className={TD + " font-medium"}>{r.name || "—"}</td>
              <td className={TD}>{r.postcode || <span style={muted}>Blank</span>}</td>
              <td className={TD}>{r.detected_postcode || <span style={{ color: "#B42318" }}>Not detected</span>}</td>
              <td className={TD}>{issues.map(([t, l]) => <Pill key={l} tone={t}>{l}</Pill>)}</td>
            </tr>))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function CorrectionsTable({ rows, canEdit, onSave }) {
  const [edits, setEdits] = useState({});
  return (
    <div className={`${CARD} p-5`} data-testid="cell-dq-corrections-section">
      <SectionHead title="Postcode corrections" hint="Detected postcodes and shared corrections, linked to the original source row. Saving updates the record for all leaders." count={rows.length} />
      {rows.length === 0 ? <Empty>All postcodes are clean.</Empty> : (
        <div className="overflow-x-auto"><table className="w-full min-w-[720px]">
          <thead><tr style={{ background: "#F7F6F3", color: "var(--sj-text-3)" }}><th className={TH}>Row</th><th className={TH}>Name</th><th className={TH}>Original entry</th><th className={TH}>Detected postcode</th>{canEdit && <th className={TH}>Correction</th>}</tr></thead>
          <tbody>{rows.map((r) => {
            const val = edits[r.record_id] ?? r.detected_postcode ?? "";
            return (
              <tr key={r.record_id} className="border-t" style={{ borderColor: "var(--sj-border)" }} data-testid={`cell-dq-correction-row-${r.record_id}`}>
                <td className={TD} style={muted}>{rowLabel(r)}</td>
                <td className={TD + " font-medium"}>{r.name}</td>
                <td className={TD}><span className="break-words">{r.postcode || <span style={muted}>Blank</span>}</span>{r.postcode_issue === "invalid" && r.detected_postcode && <div className="text-[11px]" style={muted}>Found in address</div>}</td>
                <td className={TD}>{r.detected_postcode ? <span className="font-medium" style={{ color: "#0B5E4E" }}>{r.detected_postcode}</span> : <span style={{ color: "#B42318" }}>Not detected</span>}</td>
                {canEdit && <td className={TD}><span className="flex items-center gap-1.5">
                  <input data-testid={`cell-dq-input-${r.record_id}`} value={val} onChange={(e) => setEdits({ ...edits, [r.record_id]: e.target.value.toUpperCase() })} placeholder="e.g. DA8 1AA" className={inputCls + " w-28"} />
                  <button data-testid={`cell-dq-save-${r.record_id}`} disabled={!val.trim()} onClick={() => onSave(r, val)} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-white disabled:opacity-40" style={{ background: "var(--sj-primary)" }}><Check className="h-4 w-4" /> Save</button>
                </span></td>}
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
    </div>
  );
}

const oldest = (recs) => [...recs].sort((a, b) => (a.created_at || "9").localeCompare(b.created_at || "9") || (a.source_row || 1e9) - (b.source_row || 1e9))[0];

function DuplicateGroups({ groups, canEdit, onExclude, onDismiss, onMerge }) {
  return (
    <div className={`${CARD} p-5`} data-testid="cell-dq-duplicates-section">
      <SectionHead title="Possible duplicate groups" hint="Potential matches identified through normalised email addresses or telephone numbers." count={groups.length} />
      {groups.length === 0 ? <Empty>No possible duplicates.</Empty> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {groups.map((g) => { const keep = oldest(g.records); return (
            <div key={g.key} className="rounded-xl border p-4" style={{ borderColor: "var(--sj-border)", background: "#FCFBF9" }} data-testid={`cell-dq-dup-group-${g.key}`}>
              <div className="flex items-center gap-2 text-sm font-semibold">
                {g.type === "email" ? <Mail className="h-4 w-4" style={{ color: "#B45309" }} /> : <Phone className="h-4 w-4" style={{ color: "#B45309" }} />}
                Same {g.type} · {g.records.length} records
              </div>
              <div className="text-xs mt-0.5 mb-2" style={muted}>{maskVal(g.type, g.value)}</div>
              <div className="space-y-2">
                {g.records.map((x) => (
                  <div key={x.record_id} className="flex items-center justify-between gap-2 border-t pt-2" style={{ borderColor: "var(--sj-border)" }}>
                    <span className="text-sm">Row {rowLabel(x)}: <span className="font-medium">{x.name}</span> <span style={muted}>· {x.postcode || "no postcode"}</span>{x.record_id === keep.record_id && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#E3F1EC", color: "#0B5E4E" }}>Oldest · kept on merge</span>}</span>
                    {canEdit && <button data-testid={`cell-dq-exclude-${g.key}-${x.record_id}`} onClick={() => onExclude(x)} className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium shrink-0" style={{ borderColor: "#F3B4AE", color: "#B42318" }}><UserX className="h-3.5 w-3.5" /> Exclude record</button>}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Link data-testid={`cell-dq-compare-${g.key}`} to={`/cell/responses?q=${encodeURIComponent(g.value)}`} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-medium" style={{ borderColor: "#B7C8F2", color: "#1D4ED8" }}><Search className="h-3.5 w-3.5" /> Compare in responses</Link>
                {canEdit && <button data-testid={`cell-dq-merge-${g.key}`} onClick={() => onMerge(g, keep)} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-white" style={{ background: "var(--sj-primary)" }}><GitMerge className="h-3.5 w-3.5" /> Merge into oldest</button>}
                {canEdit && <button data-testid={`cell-dq-dismiss-${g.key}`} onClick={() => onDismiss(g)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><ShieldCheck className="h-3.5 w-3.5" /> Not a duplicate</button>}
              </div>
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}

function ReviewHistory({ dismissed, excluded, canEdit, onUndismiss, onRestore }) {
  const Row = ({ id, title, sub, onUndo }) => (
    <div className="flex items-center justify-between gap-2 border-t py-2" style={{ borderColor: "var(--sj-border)" }} data-testid={id}>
      <div><div className="text-sm font-medium">{title}</div><div className="text-[11px]" style={muted}>{sub}</div></div>
      {canEdit && <button data-testid={`${id}-restore`} onClick={onUndo} className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><RotateCcw className="h-3.5 w-3.5" /> Restore</button>}
    </div>
  );
  return (
    <div className={`${CARD} p-5`} data-testid="cell-dq-history-section">
      <SectionHead title="Duplicate review history" hint="Previous decisions remain available for review and restoration." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div data-testid="cell-dq-history-separate">
          <h4 className="text-sm font-semibold mb-1">Confirmed as separate records</h4>
          {dismissed.length === 0 ? <p className="text-sm" style={muted}>No groups have been confirmed as separate records.</p> : dismissed.map((d) => (
            <Row key={d.key} id={`cell-dq-dismissed-${d.key}`} title={`Same ${d.type}: ${(d.names || []).join(", ") || maskVal(d.type, d.value || "")}`} sub={`Confirmed by ${d.decided_by || "—"} · ${fmt(d.decided_at)}`} onUndo={() => onUndismiss(d)} />
          ))}
        </div>
        <div data-testid="cell-dq-history-excluded">
          <h4 className="text-sm font-semibold mb-1">Excluded &amp; merged records</h4>
          {excluded.length === 0 ? <p className="text-sm" style={muted}>No duplicate records have been excluded.</p> : excluded.map((x) => (
            <Row key={x.record_id} id={`cell-dq-excluded-${x.record_id}`} title={`Row ${rowLabel(x)}: ${x.name}`} sub={`${x.merged_into_name ? `Merged into ${x.merged_into_name}` : "Excluded"} by ${x.excluded_by || "—"} · ${fmt(x.excluded_at)}`} onUndo={() => onRestore(x)} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CellDataQuality() {
  const { user } = useAuth();
  const canEdit = !!user?.caps?.cell_plan_edit;
  const [rows, setRows] = useState([]);
  const [dups, setDups] = useState({ groups: [], dismissed: [], excluded: [] });
  const [loading, setLoading] = useState(true);
  const [loadedAt, setLoadedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/cell/responses"), api.get("/cell/duplicates")]);
      setRows(a.data.responses || []); setDups(b.data); setLoadedAt(new Date());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const data = useMemo(() => {
    const active = rows.filter((r) => !r.excluded);
    const review = active.map((r) => {
      const issues = [];
      if (r.postcode_issue === "invalid") issues.push(["red", r.postcode ? "Invalid postcode" : "Missing postcode"]);
      if (r.postcode_issue === "extra_text") issues.push(["blue", "Extra address text"]);
      if (r.is_duplicate) issues.push(["amber", "Possible duplicate"]);
      if (!r.availability) issues.push(["amber", "Availability missing"]);
      if (!r.name) issues.push(["red", "Name missing"]);
      return { r, issues };
    }).filter((x) => x.issues.length).sort((a, b) => (a.r.source_row || 1e9) - (b.r.source_row || 1e9));
    return {
      active, review,
      corrections: active.filter((r) => r.postcode_issue).sort((a, b) => (a.source_row || 1e9) - (b.source_row || 1e9)),
      complete: active.length - review.length,
      invalid: active.filter((r) => r.postcode_issue === "invalid").length,
      extra: active.filter((r) => r.postcode_issue === "extra_text").length,
      dupRecords: active.filter((r) => r.is_duplicate).length,
      noAvail: active.filter((r) => !r.availability).length,
    };
  }, [rows]);

  const summary = `Streams of Joy Erith London — Cell Group data quality (${fmt(loadedAt)}).\n` +
    `${data.active.length} active responses: ${data.complete} complete, ${data.invalid} invalid postcode(s), ` +
    `${data.extra} with extra address text, ${data.dupRecords} possible duplicate record(s) in ${dups.groups.length} group(s), ` +
    `${data.noAvail} missing availability. ${dups.excluded.length} excluded, ${dups.dismissed.length} confirmed separate.`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(summary); toast.success("Summary copied"); }
    catch { toast.error("Could not copy"); }
  };

  const savePostcode = (r, val) => act(() => api.put(`/cell/responses/${r.record_id}`, { postcode: val.trim() }), "Postcode corrected");
  const exclude = (x) => { if (window.confirm(`Exclude ${x.name} (row ${rowLabel(x)}) from planning?`)) act(() => api.post(`/cell/responses/${x.record_id}/exclude`), "Record excluded"); };
  const merge = (g, keep) => {
    const others = g.records.filter((x) => x.record_id !== keep.record_id);
    if (!window.confirm(`Keep ${keep.name} (row ${rowLabel(keep)}) and merge ${others.map((x) => x.name).join(", ")} into it? Missing details will be filled in from the merged record(s).`)) return;
    act(() => api.post("/cell/responses/merge", { keep_id: keep.record_id, merge_ids: others.map((x) => x.record_id) }), "Records merged");
  };
  const dismiss = (g) => act(() => api.post("/cell/duplicates/dismiss", { key: g.key, names: g.records.map((x) => x.name) }), "Marked as separate records");
  const undismiss = (d) => act(() => api.post("/cell/duplicates/restore", { key: d.key }), "Returned to review");
  const restore = (x) => act(() => api.post(`/cell/responses/${x.record_id}/restore`), "Record restored");

  const metrics = [
    ["complete", "Complete records", data.complete, "No current quality flags", "#0B5E4E", CheckCircle2],
    ["invalid", "Invalid postcodes", data.invalid, data.invalid ? "Source correction required" : "All valid", data.invalid ? "#B42318" : "#0B5E4E", AlertTriangle],
    ["duplicates", "Possible duplicates", data.dupRecords, `${dups.groups.length} matching group${dups.groups.length === 1 ? "" : "s"}`, data.dupRecords ? "#B45309" : "#0B5E4E", Copy],
    ["extra", "Extra address text", data.extra, "Postcode detected in text", data.extra ? "#1D4ED8" : "#0B5E4E", FileWarning],
    ["availability", "Availability missing", data.noAvail, "Needed before approval", data.noAvail ? "#B45309" : "#0B5E4E", CalendarClock],
  ];

  return (
    <div className="space-y-6" data-testid="cell-data-quality-page">
      <div className="sj-hero p-7 print:text-black">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="sj-eyebrow">Streams of Joy Erith London</div>
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold mt-1">Data quality</h1>
            <p className="text-sm mt-1 opacity-90">Fix these before generating groups so everyone lands in the right place.</p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <button data-testid="cell-dq-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium bg-white/10 hover:bg-white/20 border border-white/20"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh data</button>
            <button data-testid="cell-dq-print" onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium bg-white/10 hover:bg-white/20 border border-white/20"><Printer className="h-4 w-4" /> Print summary</button>
            <button data-testid="cell-dq-copy" onClick={copy} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold" style={{ background: "#F5C542", color: "#1F2937" }}><Copy className="h-4 w-4" /> Copy summary</button>
          </div>
        </div>
        <p className="text-xs mt-4 opacity-75" data-testid="cell-dq-source">Private church data. Responses are read from the shared Cell Group database; corrections and duplicate decisions are saved centrally for all authorised leaders.{loadedAt && ` Loaded ${fmt(loadedAt)}.`}</p>
      </div>

      {loading && !loadedAt ? <div className={`${CARD} p-8 text-center`} style={muted}>Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {metrics.map(([id, label, value, sub, tone, Icon]) => <Metric key={id} id={id} label={label} value={value} sub={sub} tone={tone} Icon={Icon} />)}
          </div>
          <ReviewTable rows={data.review} />
          <CorrectionsTable rows={data.corrections} canEdit={canEdit} onSave={savePostcode} />
          <DuplicateGroups groups={dups.groups} canEdit={canEdit} onExclude={exclude} onDismiss={dismiss} onMerge={merge} />
          <ReviewHistory dismissed={dups.dismissed} excluded={dups.excluded} canEdit={canEdit} onUndismiss={undismiss} onRestore={restore} />
        </>
      )}
    </div>
  );
}
